import { useState, type FC } from "react";
import { EditOutlined, MailOutlined } from "@ant-design/icons";
import { message } from "antd";

import { Button, Input, Modal } from "../../../shared";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  getApiErrorMessage,
  getApiSuccessMessage,
} from "../../../../utils/ErrorUtils";
import { useAffiliateResetPassword } from "../../../../queries/affiliates/useAffiliateResetPassword";
import { useChangeAffiliateEmail } from "../../../../queries/affiliates/useChangeAffiliateEmail";

import styles from "./AffiliateInfo.module.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type AffiliateContactActionsProps = {
  affiliateId?: string;
  currentEmail: string | null | undefined;
};

const AffiliateContactActions: FC<AffiliateContactActionsProps> = ({
  affiliateId,
  currentEmail,
}) => {
  const [isEmailModalOpen, setEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const changeEmail = useChangeAffiliateEmail();
  const resetPassword = useAffiliateResetPassword();

  const normalizedEmail = newEmail.trim().toLowerCase();
  const isValidEmail = EMAIL_REGEX.test(normalizedEmail);

  const closeEmailModal = () => {
    if (changeEmail.isPending) return;
    setEmailModalOpen(false);
    setNewEmail("");
  };

  const handleChangeEmail = async () => {
    if (!affiliateId || !isValidEmail) return;

    try {
      const response = await changeEmail.mutateAsync({
        affiliateId,
        payload: { new_email: normalizedEmail },
      });
      const successMessage = getApiSuccessMessage(
        response,
        AppStrings.PAM_AFFILIATE_CHANGE_EMAIL_SUCCESS,
      );

      if (response.data.resend_status === "FAILED") {
        message.warning(
          getApiSuccessMessage(
            response,
            AppStrings.PAM_AFFILIATE_EMAIL_CHANGED_RESEND_FAILED,
          ),
        );
      } else {
        message.success(successMessage);
      }
      closeEmailModal();
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_AFFILIATE_CHANGE_EMAIL_FAILED),
      );
    }
  };

  const handleResendInvite = async () => {
    if (!affiliateId) return;

    try {
      const response = await resetPassword.mutateAsync({ affiliateId });
      message.success(
        getApiSuccessMessage(
          response,
          AppStrings.PAM_AFFILIATE_RESEND_INVITE_SUCCESS,
        ),
      );
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_AFFILIATE_RESEND_INVITE_FAILED),
      );
    }
  };

  return (
    <>
      <span className={styles.contactActions}>
        <Button
          intent="neutral"
          variant="ghost"
          size="small"
          iconLeft={<EditOutlined />}
          aria-label={AppStrings.PAM_AFFILIATE_CHANGE_EMAIL}
          onClick={() => setEmailModalOpen(true)}
          disabled={!affiliateId}
          data-testid="affiliate-change-email-button"
        />
        <Button
          intent="neutral"
          variant="ghost"
          size="small"
          onClick={handleResendInvite}
          loading={resetPassword.isPending}
          disabled={!affiliateId || resetPassword.isPending}
          data-testid="affiliate-resend-invite-button"
        >
          {AppStrings.PAM_AFFILIATE_RESEND_INVITE}
        </Button>
      </span>

      <Modal
        open={isEmailModalOpen}
        onClose={closeEmailModal}
        title={
          <span className={styles.titleWithIcon}>
            <MailOutlined />
            {AppStrings.PAM_AFFILIATE_CHANGE_EMAIL}
          </span>
        }
        data-testid="affiliate-change-email-modal"
        primaryAction={{
          label: AppStrings.YES_CHANGE_EMAIL,
          onClick: handleChangeEmail,
          loading: changeEmail.isPending,
          disabled: !isValidEmail || changeEmail.isPending,
        }}
        secondaryAction={{
          label: AppStrings.NO,
          onClick: closeEmailModal,
        }}
      >
        <p className={styles.confirmBody}>
          {AppStrings.PAM_AFFILIATE_CHANGE_EMAIL_WARNING}
        </p>
        <div className={styles.emailForm}>
          <span className={styles.emailFormLabel}>
            {AppStrings.CURRENT_EMAIL}
          </span>
          <span>{currentEmail ?? "—"}</span>
          <label className={styles.emailFormLabel} htmlFor="affiliate-new-email">
            {AppStrings.NEW_EMAIL}
          </label>
          <Input
            id="affiliate-new-email"
            type="email"
            placeholder={AppStrings.ENTER_NEW_EMAIL}
            value={newEmail}
            onChange={(event) => setNewEmail(event.target.value)}
            aria-invalid={Boolean(newEmail.trim() && !isValidEmail)}
          />
          {newEmail.trim() && !isValidEmail && (
            <span className={styles.emailValidation}>
              {AppStrings.PAM_AFFILIATE_INVALID_EMAIL}
            </span>
          )}
        </div>
      </Modal>
    </>
  );
};

export default AffiliateContactActions;
